<script>
import Layout from '@layouts/adminLayout.vue'
import FormItemGroup from '@components/form-item-group.vue'
import FormItem from '@components/form-item.vue'
import LoaderIcon from '@components/loader-icon.vue'
import SearchRecipientResult from '@components/network/search-recipient-result.vue'
import MottoBreadcrumbs from '@components/motto-breadcrumbs.vue'
import MottoIcon from '@components/motto-icon.vue'
import GrayHeader from '@components/gray-header.vue'
import Tooltip from '@components/tooltip.vue'
import {
  acceptMyReceivedReferral,
  declineMyReceivedReferral,
  getMyOfficeReferral,
  getMyReceivedReferral,
  getMyOfficeMlos,
  assignMyOfficeReferral,
  declineMyOfficeReferral,
} from '@src/api/networkConnect'
import moment from 'moment'
import router from '@router'
import { parseErrorMessage } from '@utils/string-helpers'
import _ from 'lodash'
import { authComputed } from '@state/helpers'
import { referralStatusTooltips } from '@utils/consts/referralStatusTooltips'
import { getOfficeReceivedStatus } from '@utils/network-connect-helpers'
import { saveDataTracking } from '@src/api/tracking'

export default {
  page: {
    title: 'Network Connect Referral',
    meta: [
      {
        name: 'description',
        content: 'The Network Connect Referral page.',
      },
    ],
  },
  components: {
    Layout,
    FormItem,
    FormItemGroup,
    MottoBreadcrumbs,
    GrayHeader,
    SearchRecipientResult,
    Tooltip,
    LoaderIcon,
    MottoIcon,
  },
  data() {
    return {
      loading: true,
      error: false,
      disableAcceptanceButtons: false,
      referral: {},
      referralCategory: null,
      isOfficeReferral: false,
      officeMlos: [],
    }
  },
  computed: {
    ...authComputed,
    primaryOfficeTradeName() {
      return this.currentUser.primaryOffice?.tradeName || ''
    },
    referralIdentifier() {
      return `Received ${this.referral.receivedAtStr}`
    },
    referralRecipients() {
      return this.referral.recipients || []
    },
    showAssignedField() {
      return this.referral.receivedUser && this.referral.receivedUser.id !== this.currentUser.id
    },
  },
  created() {
    if (!this.canManageNetworkConnect) {
      router.push({ name: 'home' })
    }
    this.loadReceivedReferral({ isOfficeReferral: this.$route.meta?.referralType === 'Offices' })
  },
  methods: {
    async loadReceivedReferral({ isOfficeReferral = false } = {}) {
      try {
        this.loading = true
        const { data: referral } = isOfficeReferral
          ? await getMyOfficeReferral({ id: this.$route.params.id })
          : await getMyReceivedReferral({ id: this.$route.params.id })
        this.isOfficeReferral = isOfficeReferral
        this.officeMlos = isOfficeReferral ? (await getMyOfficeMlos({ state: referral.state })).data?.data : []
        this.referral = {
          ...referral,
          officeReceivedStatus: getOfficeReceivedStatus(referral),
        }
        this.referralCategory = 'received'
        this.referral.receivedAtStr = moment.unix(this.referral.receivedAt).format('M/D/yyyy h:mm a').toUpperCase()
        this.loading = false
      } catch (err) {
        if (!isOfficeReferral) {
          await this.loadReceivedReferral({ isOfficeReferral: true })
          return
        }

        this.error = true
        this.loading = false
      }
    },

    getStatusTooltipText(status) {
      if (status === 'Completed') {
        status = 'CompletedReceived'
      }

      return referralStatusTooltips({
        status,
        tradeName: this.primaryOfficeTradeName,
      })
    },

    async declineReferral({ id }) {
      try {
        this.disableAcceptanceButtons = true
        await this.$bvModal
          .msgBoxConfirm('Are you sure you want to decline this referral?', {
            modalClass: 'confirmation-modal',
            title: 'Confirm',
            okTitle: 'Decline',
            okVariant: 'danger',

            centered: true,
          })
          .then(async (confirmation) => {
            if (confirmation) {
              let trackIdentifier = 'REFERRAL - USER - DECLINED'
              if (this.isOfficeReferral) {
                trackIdentifier = 'REFERRAL - OFFICE - DECLINED'
                await declineMyOfficeReferral({ id })
              } else {
                await declineMyReceivedReferral({ id })
              }
              await saveDataTracking(trackIdentifier)
              this.$root.$bvToast.toast('Referral declined successfully.', {
                toaster: 'b-toaster-bottom-center',
                toastClass: 'toast-success',
              })

              await router.push({
                name: 'network-connect',
                params: {
                  selectedTab: this.isOfficeReferral ? 'office_received' : 'received',
                },
              })
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.disableAcceptanceButtons = false
      }
    },

    async acceptReferral({ id }) {
      try {
        const confirmationMessage = this.referral.isSingleUserRecipient
          ? 'Are you sure you want to accept this referral?  This will complete the referral and your Work line and Motto email will be displayed for the Sender.'
          : `Are you sure you want to accept this referral?  Your Work line and Motto email will be displayed for the Sender if they choose you to complete the referral.`
        this.disableAcceptanceButtons = true
        await this.$bvModal
          .msgBoxConfirm(confirmationMessage, {
            modalClass: 'confirmation-modal',
            title: 'Confirm',
            okTitle: 'Accept',
            centered: true,
          })
          .then(async (confirmation) => {
            if (confirmation) {
              await acceptMyReceivedReferral({ id })
              await saveDataTracking('REFERRAL - ACCEPTED')
              if (this.referral.isSingleUserRecipient) {
                await this.showCompleteReferralToastAndRedirect()
                return
              }
              this.$root.$bvToast.toast('Referral accepted successfully.', {
                toaster: 'b-toaster-bottom-center',
                toastClass: 'toast-success',
              })

              await router.push({
                name: 'network-connect',
                params: {
                  selectedTab: this.isOfficeReferral ? 'office_received' : 'received',
                },
              })
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.disableAcceptanceButtons = false
      }
    },

    async showCompleteReferralToastAndRedirect() {
      const h = this.$createElement
      const vNodesMsg = h(
        'h4',
        {
          class: ['color-white', 'mb-0'],
          style: { lineHeight: '25px' },
        },
        [
          `The referral has been completed.  You may now contact the Sender using their Motto Email`,
          h('MottoIcon', {
            props: {
              name: 'Envelope',
              'icon-color': 'orange',
            },
            class: ['mx-2'],
          }),
          `or Work Line`,
          h('MottoIcon', {
            props: {
              name: 'Phone',
              'icon-color': 'orange',
            },
            class: ['mx-2'],
          }),
        ]
      )
      this.$root.$bvToast.toast([vNodesMsg], {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-primary-blue',
        autoHideDelay: 10000,
      })

      await router.push({
        name: 'network-connect',
        params: {
          selectedTab: this.isOfficeReferral ? 'office_received' : 'received',
        },
      })
    },

    async acceptMlo({ id }) {
      try {
        this.disableAcceptanceButtons = true
        const recipientName = _.find(this.officeMlos, { id }).name
        await this.$bvModal
          .msgBoxConfirm(
            `Are you sure you want to assign this referral?  This will accept the referral on the behalf of your selected MLO and display their Work line and Motto Email to the Sender if they are chosen.`,
            {
              modalClass: 'confirmation-modal',
              title: 'Confirm',
              okTitle: 'Accept',
              centered: true,
            }
          )
          .then(async (confirmation) => {
            if (confirmation) {
              await assignMyOfficeReferral({ id: this.referral.id }, id)
              await saveDataTracking('REFERRAL - OFFICE ASSIGN')
              this.$root.$bvToast.toast(
                `${recipientName} assigned
        successfully.`,
                {
                  toaster: 'b-toaster-bottom-center',
                  toastClass: 'toast-success',
                }
              )

              await router.push({
                name: 'network-connect',
                params: {
                  selectedTab: 'office_received',
                },
              })
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.disableAcceptanceButtons = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon
      v-if="loading || error"
      :error="error"
      error-message="This referral is no longer available, either because it expired or another MLO was selected.  It has been removed from Network Connect."
      class="m-auto"></LoaderIcon>
    <div v-else>
      <GrayHeader>
        <MottoBreadcrumbs
          :items="[
            {
              label: 'Network Connect',
              routeName: 'network-connect',
              routeParams: { selectedTab: isOfficeReferral ? 'office_received' : 'received' },
            },
            {
              label: `Referral - ${referralIdentifier}`,
            },
          ]"
          :back-route="'network-connect'"
          :back-route-params="{ selectedTab: isOfficeReferral ? 'office_received' : 'received' }" />
        <BaseTitle class="gray-header-title">Referral - {{ referralIdentifier }}</BaseTitle>
        <p class="d-block d-lg-none mt-3 color-secondary-light-blue">
          {{ getStatusTooltipText(referral.officeReceivedStatus || referral.receivedStatus) }}
        </p>
      </GrayHeader>
      <FormItemGroup>
        <FormItem label="Subject Property Address State" :value="referral.state"></FormItem>
        <FormItem label="Loan Type" :value="referral.loanType"></FormItem>
        <FormItem label="Loan Amount" :value="referral.loanAmount"></FormItem>
      </FormItemGroup>
      <FormItemGroup>
        <FormItem label="Date Received" :value="referral.receivedAtStr"></FormItem>
        <div class="position-relative d-flex">
          <FormItem label="Status" :value="referral.officeReceivedStatus || referral.receivedStatus"></FormItem>
          <Tooltip
            v-if="getStatusTooltipText(referral.officeReceivedStatus || referral.receivedStatus)"
            :target="`${referral.id}`"
            placement="top"
            theme="blue"
            :class="$style.statusTooltip">
            {{ getStatusTooltipText(referral.officeReceivedStatus || referral.receivedStatus) }}
          </Tooltip>
        </div>
        <div></div>
      </FormItemGroup>
      <FormItemGroup v-if="referral.receivedStatus === 'Completed'">
        <FormItem label="Sender Name" :value="referral.createdBy.name"></FormItem>
        <FormItem label="Sender Motto Email" :value="referral.createdBy.email">
          <template v-slot:afterValue>
            <a
              v-if="referral.receivedStatus === 'Completed'"
              :href="`mailto:${referral.createdBy.email}?subject=Completed ${referral.state || ''} ${
                referral.loanType || ''
              } deal level referral`"
              :class="$style.contactIcon">
              <MottoIcon :id="`envelope${referral.id}`" name="Envelope" icon-color="orange" class="ml-2"></MottoIcon>
              <b-tooltip :target="`envelope${referral.id}`" :placement="`top`" custom-class="tooltipBlue">
                Email referrer
              </b-tooltip>
            </a>
          </template>
        </FormItem>

        <FormItem label="Sender Work line" :value="referral.createdBy.phone">
          <template v-slot:afterValue>
            <a
              v-if="referral.receivedStatus === 'Completed'"
              :href="`tel:${referral.createdBy.phone}`"
              :class="$style.contactIcon">
              <MottoIcon :id="`phone${referral.id}`" name="Phone" icon-color="orange" class="ml-2"></MottoIcon>
              <b-tooltip :target="`phone${referral.id}`" :placement="`top`" custom-class="tooltipBlue">
                Call referrer’s work line
              </b-tooltip>
            </a>
          </template>
        </FormItem>
      </FormItemGroup>
      <div v-if="referral.officeUsers && referral.officeUsers.length">
        <div class="position-relative">
          <p class="p1 mt-10 mb-10 color-dark-gray">Assigned Users</p>
          <p :class="$style.recipientResponseText" class="p1 color-dark-gray">Status:</p>
          <div :class="$style.searchResultsContainer">
            <SearchRecipientResult
              v-for="user in referral.officeUsers.map((item) => ({
                ...item,
                recipientType: 'user',
              }))"
              :key="user.id"
              :recipient="user"
              disabled>
              <template v-slot:actionButton>
                <div :class="$style.selectButtonContainer">
                  <p class="text-align-left p1 color-dark-gray">
                    {{ user.receivedStatus }}
                  </p>
                </div>
              </template>
            </SearchRecipientResult>
          </div>
        </div>
      </div>
      <div v-if="referral.receivedStatus === 'Pending Assignment'">
        <p class="p1 mt-10 mb-10 color-dark-gray">Assign an MLO:</p>
        <div :class="$style.searchResultsContainer">
          <SearchRecipientResult
            v-for="mlo in officeMlos.map((item) => ({
              ...item,
              recipientType: 'user',
            }))"
            :key="mlo.id"
            :recipient="mlo"
            disabled>
            <template v-slot:actionButton>
              <BaseButton
                :options="{ defaultPadding: true }"
                class="mr-3"
                theme="green"
                :disabled="disableAcceptanceButtons"
                @click="acceptMlo(mlo)">
                Assign
              </BaseButton>
            </template>
          </SearchRecipientResult>
        </div>
      </div>
      <div
        v-if="
          (referral.receivedStatus === 'Open' && !isOfficeReferral) || referral.receivedStatus === 'Pending Assignment'
        "
        :class="$style.bottomButtonsContainer">
        <BaseButton
          :options="{ defaultPadding: true }"
          theme="red"
          :disabled="disableAcceptanceButtons"
          @click="declineReferral(referral)">
          Decline
        </BaseButton>
        <BaseButton
          v-if="referral.receivedStatus !== 'Pending Assignment'"
          :options="{ defaultPadding: true }"
          theme="green"
          :disabled="disableAcceptanceButtons"
          @click="acceptReferral(referral)">
          Accept
        </BaseButton>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.statusTooltip {
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 5px;
  margin-left: 10px;

  > div {
    position: relative;
    top: unset;
    right: unset;
  }
}

.selectedRecipientsTags {
  display: flex;
  flex-wrap: wrap;
  min-height: 43px;

  > div {
    margin-right: 6px;
    margin-bottom: 6px;
  }
}

.searchResultsContainer {
  max-height: 300px;
  padding: 10px;
  margin-bottom: 20px;
  overflow-y: scroll;
  border: 1px solid $gray;
  border-radius: 8px;

  > div {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.bottomButtonsContainer {
  @media (max-width: $lg-breakpoint) {
    margin-top: 32px;
    margin-right: 0;
  }

  display: flex;
  justify-content: center;
  margin-top: 40px;
  margin-right: 160px;

  button:first-child {
    margin-right: 24px;
  }
}

.contactIcon {
  color: unset;
}

.recipientResponseText {
  @media (max-width: $md-breakpoint) {
    display: none;
  }

  @media (max-width: $lg-breakpoint) {
    right: 155px;
  }

  position: absolute;
  top: 0;
  right: 145px;
}

.selectButtonContainer {
  @media (max-width: $lg-breakpoint) {
    width: 150px;
    min-width: 150px;
    margin: 0 10px 5px 10px;
  }

  @media (max-width: $md-breakpoint) {
    width: 100px;
    min-width: 100px;
  }

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 150px;
}
</style>
